import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  ShieldAlert,
  AlertTriangle,
  Clock,
  CalendarDays,
  Activity,
  TrendingUp,
  RefreshCw,
  Download,
} from 'lucide-react';
import { theme } from '../../../theme';
import { generateDashboardPdf } from '../services/dashboardPdfExport';
import { fetchDashboardMetrics } from '../services/dashboardService';
import { MetricCard } from '../components/MetricCard';
import { OutcomeBreakdown } from '../components/OutcomeBreakdown';
import { StatusBreakdown } from '../components/StatusBreakdown';
import { ActivityChart } from '../components/ActivityChart';
import { TopItemsList } from '../components/TopItemsList';
import type { DashboardMetrics } from '../types/dashboard.types';

const formatDuration = (seconds: number | null): string => {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

export const DashboardPage = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMetrics = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchDashboardMetrics();
      setMetrics(data);
    } catch (err) {
      console.error('Failed to load dashboard metrics:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    if (!metrics) return;
    setIsExporting(true);
    try {
      await generateDashboardPdf(metrics);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    loadMetrics();
  }, []);

  // Skeleton card
  const SkeletonCard = ({ className = '' }: { className?: string }) => (
    <div className={`${theme.card.padded} animate-pulse ${className}`}>
      <div className="h-3 w-24 rounded dark:bg-white/[0.06] bg-gray-200 mb-3" />
      <div className="h-7 w-16 rounded dark:bg-white/[0.06] bg-gray-200 mb-2" />
      <div className="h-2 w-20 rounded dark:bg-white/[0.04] bg-gray-100" />
    </div>
  );

  const SkeletonChartCard = () => (
    <div className={`${theme.card.padded} animate-pulse`}>
      <div className="h-3 w-32 rounded dark:bg-white/[0.06] bg-gray-200 mb-6" />
      <div className="flex items-end gap-1 h-36">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 rounded-t dark:bg-white/[0.04] bg-gray-100"
            style={{ height: `${Math.random() * 80 + 10}%` }}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className={theme.layout.containerMd}>
      {/* Header */}
      <div className={`mb-8 ${theme.anim.fadeInUp}`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className={`${theme.text.accentLabel} mb-2`}>Analytics</p>
            <h1 className={`text-3xl font-bold ${theme.text.heading} tracking-tight`}>
              Assessment Dashboard
            </h1>
            <p className={`text-base ${theme.text.body} mt-1`}>
              Overview of sideline assessment metrics and trends
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={isExporting || isLoading}
              className={`${theme.button.medOutline} !w-auto flex items-center gap-2 text-sm p-2 rounded-sm`}
            >
              <Download className={`w-4 h-4 ${isExporting ? 'animate-pulse' : ''}`} />
              {isExporting ? 'Exporting...' : 'Export PDF'}
            </button>
            <button
              onClick={loadMetrics}
              disabled={isLoading}
              className={`${theme.button.medOutline} !w-auto flex items-center gap-2 text-sm p-2 rounded-sm`}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className={`${theme.status.error} mb-6 flex items-center gap-3`}>
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className={`text-sm ${theme.text.heading}`}>{error}</p>
        </div>
      )}

      {/* Loading state */}
      {isLoading && !metrics && (
        <div className="space-y-6 animate-fadeInUp">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <SkeletonChartCard />
            <SkeletonCard className="h-64" />
            <SkeletonCard className="h-64" />
          </div>
        </div>
      )}

      {/* Metrics content */}
      {metrics && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-fadeInUp">
            <MetricCard
              label="Total Assessments"
              value={metrics.totalAssessments}
              icon={<BarChart3 className="w-4 h-4 dark:text-sy-teal text-teal-600" />}
            />
            <MetricCard
              label="Red Flags Detected"
              value={metrics.redFlagCount}
              icon={<ShieldAlert className="w-4 h-4 text-sy-red" />}
              accent="w-8 h-8 rounded-xl dark:bg-sy-red/[0.12] bg-red-50 dark:border-sy-red/[0.20] border border-red-100/80 flex items-center justify-center flex-shrink-0"
              subtitle={metrics.totalAssessments > 0
                ? `${Math.round((metrics.redFlagCount / metrics.totalAssessments) * 100)}% of assessments`
                : undefined}
            />
            <MetricCard
              label="This Week"
              value={metrics.assessmentsThisWeek}
              icon={<CalendarDays className="w-4 h-4 dark:text-sy-teal text-teal-600" />}
              subtitle="Assessments conducted"
            />
            <MetricCard
              label="Avg Duration"
              value={formatDuration(metrics.avgDurationSeconds)}
              icon={<Clock className="w-4 h-4 dark:text-sy-teal text-teal-600" />}
              subtitle="Per assessment"
            />
          </div>

          {/* Activity chart + Outcome + Status (3 columns on desktop) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 animate-fadeInUp animation-delay-100">
            <div className="lg:col-span-2">
              <ActivityChart dailyCounts={metrics.dailyCounts} />
            </div>
            <OutcomeBreakdown
              outcomeCounts={metrics.outcomeCounts}
              total={metrics.totalAssessments}
            />
          </div>

          {/* Status pipeline */}
          <div className="animate-fadeInUp animation-delay-200">
            <StatusBreakdown
              statusCounts={metrics.statusCounts}
              total={metrics.totalAssessments}
            />
          </div>

          {/* Top symptoms / flags / signs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fadeInUp animation-delay-300">
            <TopItemsList
              title="Top Red Flags"
              items={metrics.topRedFlags}
              accentColor="dark:text-sy-red text-red-600"
              barColor="dark:bg-sy-red bg-red-500"
            />
            <TopItemsList
              title="Top Observable Signs"
              items={metrics.topObservableSigns}
              accentColor="dark:text-amber-400 text-amber-600"
              barColor="dark:bg-amber-500 bg-amber-500"
            />
            <TopItemsList
              title="Top Reported Symptoms"
              items={metrics.topSymptoms}
              accentColor="dark:text-sy-teal text-teal-600"
              barColor="dark:bg-sy-teal bg-teal-500"
            />
          </div>

          {/* Quick links */}
          <div className={`${theme.card.paddedMuted} flex flex-wrap items-center justify-between gap-4 animate-fadeInUp animation-delay-300`}>
            <div>
              <p className={`text-sm font-medium ${theme.text.heading}`}>Need more detail?</p>
              <p className={`text-xs ${theme.text.muted}`}>View individual assessment records and case management</p>
            </div>
            <Link
              to="/assessments"
              className={`${theme.button.primaryTeal} flex items-center gap-2 text-sm`}
            >
              <Activity className="w-4 h-4" />
              View Assessment History
            </Link>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && metrics && metrics.totalAssessments === 0 && (
        <div className={`${theme.card.paddedMuted} text-center py-16 animate-fadeInUp`}>
          <div className={theme.icon.tealLg + ' mx-auto mb-4'}>
            <TrendingUp className="w-8 h-8 dark:text-sy-teal text-teal-600" />
          </div>
          <h2 className={`text-lg font-semibold ${theme.text.heading} mb-2`}>No assessment data yet</h2>
          <p className={`text-sm ${theme.text.body} mb-6 max-w-md mx-auto`}>
            Complete your first sideline assessment to start seeing metrics and trends here.
          </p>
          <Link to="/assessment" className={`${theme.button.primaryTeal} inline-flex items-center gap-2`}>
            <ShieldAlert className="w-4 h-4" />
            Start Assessment
          </Link>
        </div>
      )}
    </div>
  );
};
